/**
 * Acceso efectivo: dominios que se leen, orígenes de envío, orígenes de listas
 * y si el resumen es visible. El arrastre listas → envío se sostiene aquí.
 * Un usuario no declarado lee el catálogo de dominios entero y no tiene orígenes.
 */
const DOMINIOS_RESUMEN = ['Red', 'IT', 'ZTE'];
const VISTAS_POR_CODIGO = {
  Red: ['combinado', 'red'],
  ZTE: ['combinado', 'red'],
  IT: ['combinado', 'it'],
};

function enOrden(catalogo, concedidos) {
  const granted = new Set(concedidos || []);
  return (catalogo || []).filter(function (codigo) {
    return granted.has(codigo);
  });
}

function accesoEfectivo({
  catalogoDominios,
  catalogoOrigenes,
  declarado,
  concesionesDominio,
  concesionesEnvio,
  concesionesListas,
} = {}) {
  const dominios = declarado
    ? enOrden(catalogoDominios, concesionesDominio)
    : (catalogoDominios || []).slice();
  const origenesListas = declarado
    ? enOrden(catalogoOrigenes, concesionesListas)
    : [];
  const origenes = declarado
    ? enOrden(catalogoOrigenes, (concesionesEnvio || []).concat(origenesListas))
    : [];
  const resumen = DOMINIOS_RESUMEN.every(function (codigo) {
    return dominios.indexOf(codigo) !== -1;
  });
  return {
    dominios: dominios,
    origenes: origenes,
    origenesListas: origenesListas,
    resumen: resumen,
    menus: menusDeAcceso(dominios, origenes, origenesListas),
  };
}

function menusDeAcceso(dominios, origenes, origenesListas) {
  return Object.assign(menusDeDominios(dominios), {
    envio: origenes.length > 0,
    listas: origenesListas.length > 0,
  });
}

function menusDeDominios(dominios) {
  const lee = dominios || [];
  return {
    redIt: lee.length > 0,
    red: lee.indexOf('Red') !== -1 || lee.indexOf('ZTE') !== -1,
    it: lee.indexOf('IT') !== -1,
  };
}

function vistasDeConcesion(codigo) {
  return VISTAS_POR_CODIGO[codigo] || ['combinado'];
}

/**
 * Clasifica el dominio almacenado de una incidencia.
 * Red y Red/Huawei se leen con la concesión Red.
 * Un código del catálogo se lee con esa concesión.
 * Cualquier otro valor queda oculto para todos.
 */
function clasificarDominioAlmacenado(dominioAlmacenado, catalogoDominios) {
  const catalogo = catalogoDominios || [];
  if (dominioAlmacenado === 'Red/Huawei') {
    return {concesion: 'Red', vistas: vistasDeConcesion('Red')};
  }
  if (catalogo.indexOf(dominioAlmacenado) === -1) {
    return null;
  }
  return {
    concesion: dominioAlmacenado,
    vistas: vistasDeConcesion(dominioAlmacenado),
  };
}

function puedeLeerDominioAlmacenado(dominioAlmacenado, {catalogoDominios, dominios} = {}) {
  const clase = clasificarDominioAlmacenado(dominioAlmacenado, catalogoDominios);
  if (!clase) return false;
  return (dominios || []).indexOf(clase.concesion) !== -1;
}

/**
 * Un editor modifica, borra o recupera solo una incidencia cuyo dominio lee.
 * Sin el indicador de editor no actúa, aunque lea ese dominio.
 */
function puedeActuarSobreIncidencia(dominioAlmacenado, {catalogoDominios, dominios, editor} = {}) {
  if (Number(editor) !== 1) return false;
  return puedeLeerDominioAlmacenado(dominioAlmacenado, {catalogoDominios, dominios});
}

function valoresAlmacenadosVisibles({catalogoDominios, dominios, vista} = {}) {
  const catalogo = catalogoDominios || [];
  const candidatos = [];
  catalogo.forEach(function (codigo) {
    candidatos.push(codigo);
    if (codigo === 'Red') candidatos.push('Red/Huawei');
  });
  return candidatos.filter(function (valor) {
    const clase = clasificarDominioAlmacenado(valor, catalogo);
    if (!clase) return false;
    if ((dominios || []).indexOf(clase.concesion) === -1) return false;
    return clase.vistas.indexOf(vista) !== -1;
  });
}

function identificadorMensajeDe(resultado) {
  if (!resultado) return null;
  if (Object.prototype.hasOwnProperty.call(resultado, 'identificadorMensaje')) {
    return resultado.identificadorMensaje;
  }
  const ticket = resultado.ticket;
  if (ticket && Object.prototype.hasOwnProperty.call(ticket, 'message_id')) {
    return ticket.message_id;
  }
  return null;
}

function dominioDeAlarma(resultado) {
  if (resultado.dominio != null) return resultado.dominio;
  if (resultado.Dominio != null) return resultado.Dominio;
  return 'IT';
}

/**
 * Familia de un resultado del buscador.
 * Una alarma IT, también la nacida del formulario IT, exige IT.
 * Un ticket ETL con identificador de mensaje exige Red.
 * Un ticket ETL sin identificador de mensaje exige ZTE.
 * Una alarma ZTE nacida del formulario queda fuera: no tiene familia.
 */
function familiaDeResultadoBuscador(resultado) {
  if (!resultado) return null;
  const fuente = resultado.source || resultado.fuente;
  if (fuente === 'it_alarm' || fuente === 'alarma') {
    return dominioDeAlarma(resultado) === 'IT' ? 'IT' : null;
  }
  if (fuente === 'etlred' || fuente === 'etl') {
    const identificador = identificadorMensajeDe(resultado);
    if (identificador == null || String(identificador).trim() === '') return 'ZTE';
    return 'Red';
  }
  return null;
}

function puedeLeerResultadoBuscador(resultado, dominios) {
  const familia = familiaDeResultadoBuscador(resultado);
  if (!familia) return false;
  return (dominios || []).indexOf(familia) !== -1;
}

function vistaDeTipo(tipo) {
  const value = String(tipo || '').trim().toLowerCase();
  if (!value) return 'combinado';
  if (value === 'red' || value === 'it') return value;
  return null;
}

/**
 * Arrastre al guardar un interruptor de origen.
 * Conceder listas concede el envío. Quitar el envío quita las listas.
 * Quitar las listas deja el envío.
 */
function aplicarArrastre({catalogoOrigenes, envio, listas, tipo, codigo, activo}) {
  const envioNext = new Set(envio || []);
  const listasNext = new Set(listas || []);
  const on = Number(activo) === 1;
  if (tipo === 'envio') {
    if (on) {
      envioNext.add(codigo);
    } else {
      envioNext.delete(codigo);
      listasNext.delete(codigo);
    }
  } else if (tipo === 'listas') {
    if (on) {
      listasNext.add(codigo);
      envioNext.add(codigo);
    } else {
      listasNext.delete(codigo);
    }
  }
  return {
    envio: enOrden(catalogoOrigenes, envioNext),
    listas: enOrden(catalogoOrigenes, listasNext),
  };
}

function concederLectura({catalogoDominios, dominios, codigo, activo}) {
  const next = new Set(dominios || []);
  if (Number(activo) === 1) {
    next.add(codigo);
  } else {
    next.delete(codigo);
  }
  return enOrden(catalogoDominios, next);
}

module.exports = {
  DOMINIOS_RESUMEN,
  accesoEfectivo,
  aplicarArrastre,
  concederLectura,
  clasificarDominioAlmacenado,
  puedeLeerDominioAlmacenado,
  puedeActuarSobreIncidencia,
  familiaDeResultadoBuscador,
  puedeLeerResultadoBuscador,
  valoresAlmacenadosVisibles,
  vistaDeTipo,
};
